
import React from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Sparkles, Bell, FileText } from 'lucide-react';

interface Generation {
  id: string;
  content: string;
  type: string;
  created_at: string;
}

interface GenerationHistoryProps {
  generations: Generation[];
  contentTypeLabels: Record<string, string>;
  onCreateNew: () => void;
}

const GenerationHistory: React.FC<GenerationHistoryProps> = ({
  generations,
  contentTypeLabels,
  onCreateNew
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Generations</CardTitle>
        <CardDescription>
          View your recently generated content
        </CardDescription>
      </CardHeader>
      <CardContent>
        {generations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2">No recent generations</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={onCreateNew}
            >
              Create your first content
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {generations.map((item, index) => (
              <div key={item.id || index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {item.type === 'announcement' ? (
                      <LayoutDashboard className="h-4 w-4 text-blue-500" />
                    ) : item.type === 'marketing' ? (
                      <Sparkles className="h-4 w-4 text-purple-500" />
                    ) : (
                      <Bell className="h-4 w-4 text-amber-500" />
                    )}
                    <span className="font-medium">
                      {contentTypeLabels[item.type] || item.type}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(item.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{item.content}</p>
                <div className="flex justify-end mt-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(item.content);
                      toast.success('Copied to clipboard');
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GenerationHistory;
